import React, { Fragment, useState } from "react";
import get from "lodash/get";
import { FieldArray, useFormikContext } from "formik";
import Toggle from "react-toggle";
import { getFieldPath, getToggleProps, submitOnReturn } from "utils/form";
import {
  PREFILL_DEFAULT,
  PREFILL_SOURCE,
  PREFILL_SOURCE_OPTIONS
} from "utils/constants/prefill";
import isUndefined from "lodash/isUndefined";
import isNull from "lodash/isNull";
import MultiselectOption from "components/MultiselectOption";
import MultiSelect from "components/Form/fields/MultiSelect";
import DebounceInput from "components/DebounceInput";
import { SET_TOUCH_TIMEOUT } from "utils/constants/form";
import HoverIconButton from "components/HoverIconButton";
import { INPUT_CLASSES } from "utils/constants/ui";
import {
  validatePrefillQueryKey,
  validatePrefillQueryKeys
} from "utils/prefill";
import { COMMAND_CONTENT_ID } from "components/CommandPalette/constants";
import PlayVideoTrigger from "components/PlayVideoTrigger";
import { COLORS } from "utils/styles";
import classnames from "classnames";
import { STATE_KEYS } from "utils/constants/state";

const DEFAULT_LABEL = "Prefill";
const DEFAULT_DESCRIPTION = "Enable field prefill through query string params.";

const Prefill = ({
  name,
  label = DEFAULT_LABEL,
  description = DEFAULT_DESCRIPTION
}) => {
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();

  const [queryKey, setQueryKey] = useState("");

  const prefillPath = getFieldPath(name, STATE_KEYS.FIELD.PREFILL);
  const prefillConfig = get(values, prefillPath);
  const hasPrefillConfig =
    !isUndefined(prefillConfig) && !isNull(prefillConfig);

  const prefillEnabledPath = getFieldPath(
    name,
    STATE_KEYS.FIELD.PREFILL_ENABLED
  );
  const prefillEnableTouched = Boolean(get(prefillEnabledPath, touched));

  const prefillEnabled = Boolean(get(values, prefillEnabledPath));
  const prefillSourcePath = getFieldPath(name, STATE_KEYS.FIELD.PREFILL_SOURCE);
  const prefillSource = get(values, prefillSourcePath);
  const prefillSourceTouched = Boolean(get(touched, prefillSourcePath));
  const isQuerySource = prefillSource === PREFILL_SOURCE.QUERY;

  const readonlyPath = getFieldPath(name, STATE_KEYS.FIELD.PREFILL_READONLY);
  const readonlyTouched = get(touched, readonlyPath);

  const readonly = Boolean(get(values, readonlyPath));

  const queryKeysPath = getFieldPath(name, STATE_KEYS.FIELD.PREFILL_QUERY_KEYS);
  const queryKeys = get(values, queryKeysPath) || [];
  const queryKeyError = validatePrefillQueryKey({ queryKeys, queryKey });
  const queryKeysError = validatePrefillQueryKeys(queryKeys);
  const showPrefillQueryError = isQuerySource && queryKeysError;

  const selectedSource = PREFILL_SOURCE_OPTIONS.find(
    (option) => option.id === prefillSource
  );

  let sourceSelections = [];
  if (selectedSource) {
    sourceSelections = [
      {
        id: selectedSource.id,
        name: selectedSource.label
      }
    ];
  }

  const sourceSelectProps = {
    value: sourceSelections,
    disabled: !prefillEnabled,
    placeholder: "Select source",
    options: PREFILL_SOURCE_OPTIONS,
    onChange: () => ({ value }) => {
      setFieldValue(prefillSourcePath, value);

      if (!prefillSourceTouched) {
        setFieldTouched(prefillSourcePath, true);
      }
    },
    isMulti: false,
    labelTransform: (value) => value,
    components: {
      Option: MultiselectOption
    }
  };

  return (
    <Fragment>
      <div className="w-100 flex flex-column">
        <div className="w-100 flex flex-row justify-between items-center">
          <label className="f6 dib fw6">{label}</label>
          <div className="flex flex-row">
            <PlayVideoTrigger id={COMMAND_CONTENT_ID.FIELDS.PREFILL} />
            <div className="dib bl hairline-1 pl2 ml2">
              <Toggle
                {...getToggleProps({
                  active: prefillEnabled,
                  setFieldValue: (_, active) => {
                    setFieldValue(prefillEnabledPath, active);
                    const needsDefaultConfig = active && !hasPrefillConfig;
                    if (needsDefaultConfig) {
                      setFieldValue(prefillPath, PREFILL_DEFAULT);
                    }

                    if (!prefillEnableTouched) {
                      setFieldTouched(prefillEnabledPath, true);
                    }
                  }
                })}
              />
            </div>
          </div>
        </div>
        <div className="f6 pt2">{description}</div>
        {prefillEnabled && (
          <Fragment>
            <div className="pt3">
              <label className="f6 dib fw5">Source</label>
              <div className="pt2">
                <MultiSelect {...sourceSelectProps} />
              </div>
            </div>
            {prefillSource && (
              <div
                className={classnames("mt3 pv3 ph3 ba br2", {
                  "hairline-1": !showPrefillQueryError,
                  "b--error": showPrefillQueryError
                })}
              >
                {isQuerySource && (
                  <Fragment>
                    <label className="f6 dib fw5">Keys</label>
                    <div className="f6 pt2 lh-title">
                      Add one or more keys to pick from query string params for
                      this field.
                    </div>
                    <div className="pt2">
                      <FieldArray
                        name={queryKeysPath}
                        render={(arrayHelpers) => {
                          const keyNodes = queryKeys.map((key, keyIx) => {
                            const queryKeyName = `${queryKeysPath}[${keyIx}]`;

                            const keyNameTouched = get(touched, queryKeyName);

                            return (
                              <li
                                key={keyIx}
                                className="pt3 flex flex-row items-center"
                              >
                                <DebounceInput
                                  name={queryKeyName}
                                  inputValue={key}
                                  onBlur={() => {}}
                                  valueSet={(val) => {
                                    setFieldValue(queryKeyName, val);
                                    if (!keyNameTouched) {
                                      setTimeout(() => {
                                        setFieldTouched(queryKeyName, true);
                                      }, SET_TOUCH_TIMEOUT);
                                    }
                                  }}
                                />
                                <div className="dib pl1">
                                  <HoverIconButton
                                    type="delete"
                                    onClick={() => {
                                      arrayHelpers.remove(keyIx);
                                      if (!keyNameTouched) {
                                        setTimeout(() => {
                                          setFieldTouched(queryKeyName, true);
                                        }, SET_TOUCH_TIMEOUT);
                                      }
                                    }}
                                  />
                                </div>
                              </li>
                            );
                          });

                          const addKey = () => {
                            if (queryKey) {
                              arrayHelpers.push(queryKey);
                              const newIx = queryKeys.length - 1;
                              const queryKeyName = `${queryKeysPath}[${newIx}]`;
                              setTimeout(() => {
                                setFieldTouched(queryKeyName, true);
                              }, SET_TOUCH_TIMEOUT);

                              setQueryKey("");
                            }
                          };

                          return (
                            <div className="w-100">
                              <div className="flex flex-column">
                                <div className="flex flex-row items-center">
                                  <input
                                    className={INPUT_CLASSES}
                                    value={queryKey}
                                    placeholder="Add a key name"
                                    onChange={(evt) =>
                                      setQueryKey(evt.currentTarget.value)
                                    }
                                    onKeyDown={submitOnReturn({
                                      disabled: !queryKey,
                                      submitForm: addKey
                                    })}
                                  ></input>
                                  <div className="pl1">
                                    <HoverIconButton
                                      type="add"
                                      color={COLORS.blue}
                                      disabled={!queryKey}
                                      onClick={addKey}
                                    />
                                  </div>
                                </div>
                                {queryKeyError && (
                                  <div className="field__sublabel fw5 red">
                                    {queryKeyError}
                                  </div>
                                )}
                              </div>

                              <ul className="list ma0 pa0">{keyNodes}</ul>
                            </div>
                          );
                        }}
                      />
                    </div>
                    {showPrefillQueryError && (
                      <div className="field__sublabel fw5 red pt1">
                        {queryKeysError}
                      </div>
                    )}
                  </Fragment>
                )}
              </div>
            )}
            <div className="pt3">
              <div className="w-100 flex flex-row justify-between items-center">
                <label className="f6 dib fw5">Readonly</label>
                <Toggle
                  {...getToggleProps({
                    active: Boolean(readonly),
                    setFieldValue: (_, active) => {
                      setFieldValue(readonlyPath, active);

                      if (!readonlyTouched) {
                        setFieldTouched(readonlyPath, true);
                      }
                    }
                  })}
                />
              </div>
              <div className="f6 pt2">Pre-filled value will be readonly.</div>
            </div>
          </Fragment>
        )}
      </div>
    </Fragment>
  );
};

export default Prefill;
